import * as React from 'react';
import Icon from '@cx-web/components/primitives/icon';
import classNames from 'classnames';

import { type BetslipProviderData, useBetslipContext } from '~/providers/betslip-context';
import { type ServicesProviderData, useServicesContext } from '~/providers/services-context';
import { TRACKING_ACTIONS, TRACKING_CATEGORIES, TRACKING_EVENTS } from '~/services/actions/tracking-actions';

import styles from '../style/mini-betslip.module.css';

interface WithContext {
  betslipContext: BetslipProviderData;
  servicesContext: ServicesProviderData;
}

interface IMiniBetslipProps extends WithContext {
  betslipTitle: string;
  className?: string;
}

interface IMiniBetslipState {
  canRenderBetCount: boolean;
}

class MiniBetslip extends React.Component<IMiniBetslipProps, IMiniBetslipState> {
  constructor(props: IMiniBetslipProps) {
    super(props);

    this.state = {
      canRenderBetCount: false
    };
  }

  public componentDidMount(): void {
    this.setState({ canRenderBetCount: true });
  }

  public shouldComponentUpdate(nextProps: IMiniBetslipProps, nextState: IMiniBetslipState) {
    const { canRenderBetCount } = this.state;
    const { canRenderBetCount: nextCanRenderBetCount } = nextState;
    const { added, betslip, count } = this.props.betslipContext;
    const { added: nextAdded, betslip: nextBetslip, count: nextCount } = nextProps.betslipContext;

    return (
      nextCanRenderBetCount !== canRenderBetCount ||
      nextBetslip !== betslip ||
      nextCount !== count ||
      nextAdded !== added);

  }

  private handleBetslipToggle = () => {
    const { betslipContext, servicesContext } = this.props;
    const { toggleBetslip } = betslipContext;

    servicesContext.trackingActions.eventTracking({
      event: TRACKING_EVENTS.BETSLIP_ICON_CLICKED,
      eventCategory: TRACKING_CATEGORIES.BETTING,
      eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.BETSLIP_ICON_CLICKED]
    });

    const { betslip, stake } = betslipContext;
    toggleBetslip();
    betslipContext.trackBetSlip(betslip, stake);
  };

  public componentDidUpdate(prevProps: IMiniBetslipProps) {
    const { added: betAdded, count, toggleAdded } = this.props.betslipContext;
    const { count: prevCount } = prevProps.betslipContext;

    if (count > prevCount) {
      toggleAdded(true);
    }

    if (betAdded) {
      setTimeout(() => {
        toggleAdded(false);
      }, 400);
    }
  }

  private renderBetCount(): JSX.Element | null {
    const { canRenderBetCount } = this.state;
    const { added: betAdded, count: betCount } = this.props.betslipContext;
    if (!canRenderBetCount || !betCount) {
      return null;
    }

    return <span className={[styles.betCount, betAdded ? styles.betAdded : ''].join(' ')}>{betCount}</span>;
  }

  public render(): JSX.Element {
    const { betslipTitle, className } = this.props;

    return (
      <div className={styles.wrapper} data-testid="betslip-button" onClick={this.handleBetslipToggle}>
        <div className={classNames(styles.miniBetslip, className)}>
          <Icon height={20} icon="Betslip" width={20} />
          {this.renderBetCount()}
        </div>
        <span className={styles.betslipText}>{betslipTitle}</span>
      </div>);

  }
}

export function withContext<T extends WithContext = WithContext>(Component: React.ComponentType<T>) {
  const WrappedComponent = (props: Omit<T, keyof WithContext>) => {
    const betslipContext = useBetslipContext();
    const servicesContext = useServicesContext();

    return <Component {...props as T} betslipContext={betslipContext} servicesContext={servicesContext} />;
  };

  const displayName = Component.displayName || Component.name || 'Betslip';
  WrappedComponent.displayName = `withContext(${displayName})`;

  return WrappedComponent;
}

const MiniBetslipWithContextAndStoreSlices = withContext(MiniBetslip);

export default MiniBetslipWithContextAndStoreSlices;